import React, { useState } from 'react';
import { faCaretDown, faBars } from '@fortawesome/free-solid-svg-icons';
import { Menu, MenuItem } from '@material-ui/core';
import { makeBtStyles } from '../Theme';
import Hidden from '@material-ui/core/Hidden';
import Button from '@material-ui/core/Button';

import Icon from '../components/Icon';
import { useBrandingData } from '../dataProviders';
import NavDrawer, { makeNavDrawerStyles, NavDrawerProvider } from '../components/NavDrawer';
import { ContactEmail, ContactPhone, ReturnPolicyLink } from '../components/ContactInfo';
import SocialMediaGrid, { makeSocialMediaGridStyles } from '../components/SocialMediaGrid';
import NavLinks from '../components/NavLinks';
import NavLogo from '../components/NavLogo';
import { BtLink } from '../components/BtLink';


const useSocialMediaStyles = makeSocialMediaGridStyles( theme => ({
  root: {
    float: 'right',
    height: '48px'
  },
  link: {
    float: 'right',
    fontSize: '44px',
    padding: '0 0 0 32px',
    lineHeight: '33px',
    textAlign: 'center',
    '&.social-type-instagram': {
      color: '#ed2e6c'
    },
    '&.social-type-twitter': {
      color: '#33CCFF'
    },
    '&.social-type-facebook': {
      color: '#3B5997'
    },
    '& svg': {
      display: 'block',
      height: '33px',
      width: '28px !important',
      '&:hover': {
        transform: 'translate(0,4px) scale(1.25)'
      },
      [theme.breakpoints.down('xs')]: {
        height: '22px',
        width: '18px !important'
      }
    }
  }
}));


const useStyles = makeBtStyles( theme => ({
  banner: {
    paddingTop: '24px',
    height: '100px',
    width: '100%',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      paddingTop: '8px'
    }
  },
  logoWrapper: {
    height: '80px',
    position: 'absolute',
    paddingRight: '24px',
    backgroundColor: 'white',
    '& img': {
      maxHeight: '100%',
      maxWidth: '300px'
    }
  },
  logoText: {
    fontSize: '27px',
    lineHeight: '90px',
    [theme.breakpoints.down('xs')]: {
      fontSize: '25px',
      lineHeight: '30px'
    }
  },
  socialMediaWrapper: {
    height: '48px'
  },
  supportLinksWrapper: {
    borderTop: '1px solid #ddd',
    clear: 'both',
    [theme.breakpoints.down('sm')]: {
      border: 'none'
    }
  },
  navLinks: {
    marginTop: '12px',
    float: 'right',
    marginBottom: '0',
    paddingLeft: '0',
    listStyle: 'none',
  },
  navLink: {
    float: 'left',
    display: 'block',
    color: '#777',
    textDecoration: 'none',
    padding: '10px 15px',
    fontSize: '14px',
    lineHeight: '18px',
    '& .icon': {
      display: 'none' // hide icon on ReturnPolicyLink
    }
  },
  supportDropdownItem: {
    fontSize: '14px',
    '&:hover': {
      background: 'none',
      '& a': {
        color: 'black'
      }
    },
    '& a': {
      color: '#777',
      '&:hover': {
        textDecoration: 'none'
      }
    },
    '& .icon': {
      display: 'none'
    }
  },
  mobileNavOpener: {
    color: '#333',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    float: 'right',
    textTransform: 'none',
    fontWeight: '400',
    '& svg': {
      fontSize: '16px'
    },
    [theme.breakpoints.down('sm')]: {
      padding: '4px 4px 4px 6px',
      minWidth: '45px',
      '& svg': {
        fontSize: '14px'
      }
    }
  },
  drawerContactHeader: {
    display: 'block',
    padding: '10px',
  },
  drawerContactEntry: {
    display: 'block',
    color: 'white',
    padding: '10px 25px',
    fontSize: '16px',
    '&:hover': {
      textDecoration: 'none',
      backgroundColor: '#49a5df'
    },
    '& .icon': {
      display: 'none'
    }
  }
}));


const useMenuStyles = makeBtStyles( theme => ({
  paper: {
    transform: 'translate(-45%,36px) !important'
  }
}));


const useNavDrawerStyles = makeNavDrawerStyles( theme => ({
  paper: {
    backgroundColor: '#1B6A9C',
    color: 'white',
    minWidth: 0
  },
  list: {
    paddingLeft: '0',
    borderBottom: '1px solid white',
    minWidth: '250px'
  },
  item: {
    color: 'white',
    paddingLeft: '20px',
    '&:hover': {
      backgroundColor: '#49a5df'
    }
  }
}));


export default function Banner() {
  const classes = useStyles();
  const menuClasses = useMenuStyles();
  const { phone, email, store_url, toggles } = useBrandingData();

  const showStoreUrl = toggles.show_store_url && store_url
  const showPhone = toggles.show_phone && phone;
  const showEmail = toggles.show_email && email;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const navDrawerClasses = useNavDrawerStyles();
  const socialMediaClasses = useSocialMediaStyles();
  const [contactUsMenuAnchor,setContactUsMenuAnchor] = useState(null);

  const toggleContactUs = (event) => setContactUsMenuAnchor( contactUsMenuAnchor ? null : event.target );

  const toggleDrawer = (open) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  return (
    <NavDrawerProvider>
      <div className={classes.banner}>
        <div className={classes.logoWrapper}>
          <NavLogo classes={{ textLogo: classes.logoText }} />
        </div>

        <div className={classes.socialMediaWrapper}>
          <SocialMediaGrid classes={socialMediaClasses} />
        </div>

        <div className={classes.supportLinksWrapper}>
          <Hidden smDown>
            <div className={classes.navLinks}>
              { showStoreUrl &&
                <BtLink href={store_url} className={classes.navLink}>Our Website</BtLink>
              }
              <NavLinks linkClassName={classes.navLink} />
              <ReturnPolicyLink className={classes.navLink} />
              { (showPhone || showEmail) && <>
                <BtLink onClick={toggleContactUs} aria-controls="contact-us-menu" className={classes.navLink}>
                  Contact Us <Icon icon={faCaretDown} />
                </BtLink>
                <Menu id="contact-us-menu" keepMounted anchorEl={contactUsMenuAnchor} open={Boolean(contactUsMenuAnchor)} onClose={toggleContactUs} classes={menuClasses}>
                  { showEmail &&
                    <MenuItem className={classes.supportDropdownItem}>
                      <ContactEmail />
                    </MenuItem>
                  }
                  { showPhone &&
                    <MenuItem className={classes.supportDropdownItem}>
                      <ContactPhone />
                    </MenuItem>
                  }
                </Menu>
              </>}
            </div>
          </Hidden>

          <Hidden mdUp>
            <Button onClick={toggleDrawer(true)} className={classes.mobileNavOpener}>
              <Icon icon={faBars} size="2x" className={''}/>&nbsp;&nbsp;<Hidden xsDown>Service / Support</Hidden>
            </Button>
            <NavDrawer open={drawerOpen} toggleDrawer={toggleDrawer} classes={navDrawerClasses} anchor='right'>
              { ({ LinkList }) => <>
                <LinkList />
                { (showEmail || showPhone) &&
                  <label className={classes.drawerContactHeader}>Contact Information</label>
                }
                { showEmail &&
                  <ContactEmail className={classes.drawerContactEntry} />
                }
                { showPhone &&
                  <ContactPhone className={classes.drawerContactEntry} />
                }
                <ReturnPolicyLink className={classes.drawerContactEntry} />
              </>}
            </NavDrawer>
          </Hidden>
        </div>

      </div>
    </NavDrawerProvider>
  );
}